import React, { useState, useEffect, useRef } from "react";
import "./MarketingSlider.css";

// Each section = one screenshot + heading, with several short blurbs that the
// copy cycles through as you scroll. Drop images in frontend/public/screens/.
const SECTIONS = [
  {
    eyebrow: "Real-Time Visibility",
    heading: "Fix problems in the moment, not after month-end",
    image: "/screens/dashboard.png",
    mobileImage: "/screens/dashboard-mobile.png",
    caption: "Performance Dashboard",
    blurbs: [
      "Accurate, real-time performance data across every department.",
      "On web and mobile—so the right people see what matters and act before it impacts results.",
    ],
  },
  {
    eyebrow: "Accountability",
    heading: "Surface operational gaps and trends instantly",
    image: "/screens/scoreboard.png",
    caption: "Department Scoreboards",
    blurbs: [
      "Centralized, standardized data exposes underperformance and revenue opportunities the moment they appear.",
      "Improve accountability and minimize lost sales before they reach month-end.",
    ],
  },
  {
    eyebrow: "Growth",
    heading: "Data-driven budgets and group-wide visibility",
    image: "/screens/profit-summary.png",
    caption: "Budgets & Multi-Store",
    blurbs: [
      "Build annual budgets from verified historical performance.",
      "Give dealer groups shared visibility across stores to align best practices and drive long-term growth.",
    ],
  },
];

// Flatten into scroll sub-steps (one per blurb).
const STEPS = [];
SECTIONS.forEach((sec, si) =>
  sec.blurbs.forEach((blurb, bi) =>
    STEPS.push({ section: si, blurb, blurbIndex: bi })
  )
);
const N = STEPS.length;

export default function MarketingSlider() {
  const [step, setStep] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;

    const update = () => {
      ticking = false;
      const total = el.offsetHeight - window.innerHeight;
      const top = el.getBoundingClientRect().top;
      const scrolled = Math.min(Math.max(-top, 0), Math.max(total, 1));
      const progress = total > 0 ? scrolled / total : 0;
      const t = progress * (N - 1);

      // The screenshots no longer slide continuously; each section's screen
      // pops in on change (see the keyed figure + CSS entrance). We only need
      // to know which step/section we're on to swap copy and trigger the pop.
      const idx = Math.round(t);
      setStep((prev) => (prev !== idx ? idx : prev));
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Desktop autoplay: gently drive the scroll through the sequence so a passive
  // visitor always sees every message. Any manual interaction (wheel/touch/type)
  // pauses it for a beat, so scrolling forward/backward by hand still works and
  // the existing scroll handler keeps the visuals in sync either way.
  useEffect(() => {
    if (!window.matchMedia("(min-width: 920px)").matches) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;

    const STEP_MS = 3200; // time spent per blurb
    const loopMs = N * STEP_MS;
    let raf = 0;
    let base = null; // time anchor; null forces a resync to current scroll
    let pausedUntil = 0; // suspend autoplay until this timestamp

    const tick = (now) => {
      const range = el.offsetHeight - window.innerHeight;
      const trackTop = el.getBoundingClientRect().top + window.scrollY;
      if (range > 0 && now >= pausedUntil) {
        if (base === null) {
          const rel = Math.min(
            Math.max((window.scrollY - trackTop) / range, 0),
            1
          );
          base = now - rel * loopMs;
        }
        // Loop forward and wrap back to the top. The pinned (sticky) stage
        // fills the viewport across the whole track, so resetting scroll from
        // the last step back to the first doesn't move the page visibly — it
        // just replays section 1's pop-in, giving a clean repeating loop.
        const frac = ((now - base) % loopMs) / loopMs;
        window.scrollTo(0, trackTop + frac * range);
      } else {
        base = null;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const bump = () => {
      pausedUntil = performance.now() + 2500;
      base = null;
    };
    const opts = { passive: true };
    window.addEventListener("wheel", bump, opts);
    window.addEventListener("touchstart", bump, opts);
    window.addEventListener("pointerdown", bump, opts);
    window.addEventListener("keydown", bump);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("wheel", bump, opts);
      window.removeEventListener("touchstart", bump, opts);
      window.removeEventListener("pointerdown", bump, opts);
      window.removeEventListener("keydown", bump);
    };
  }, []);

  const goToSection = (si) => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = STEPS.findIndex((s) => s.section === si);
    const total = el.offsetHeight - window.innerHeight;
    const progress = idx / (N - 1);
    const top = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + progress * total, behavior: "smooth" });
  };

  const sec = STEPS[step].section;
  const section = SECTIONS[sec];
  const side = sec === 1 ? "left" : "right";
  // The copy is aligned to the opposite edge from the screenshot's bleed side.
  // Screens pop in from that same edge — i.e. from "whichever side the text is".
  const textSide = sec === 1 ? "right" : "left";

  return (
    <div className="mkt-scroll" ref={scrollRef} style={{ "--mkt-steps": N }}>
      {/* Desktop: pinned stage. Copy on top, large screenshot that slides sides. */}
      <div className={`mkt-stage side-${side}`}>
        <div className="mkt-stage-copy">
          <span className="mkt-eyebrow" key={`e-${sec}`}>
            {section.eyebrow}
          </span>
          <h3 className="mkt-title" key={`h-${sec}`}>
            {section.heading}
          </h3>
          <p className="mkt-blurb" key={`b-${step}`}>
            {STEPS[step].blurb}
          </p>
        </div>

        <div className="mkt-stage-figure" aria-hidden="true">
          {/* Keyed by section so each change remounts and replays the CSS
              pop-in entrance from the text-aligned side. */}
          <div className={`mkt-screen enter-${textSide}`} key={sec}>
            <div className="mkt-frame">
              <img className="mkt-shot" src={section.image} alt={section.caption} />
            </div>
            {/* Phone is the "second screen": it only pops in on the
                web-and-mobile blurb (blurb 2) and slides back out on the
                others, e.g. when you scroll back to the top of this section. */}
            {section.mobileImage && (
              <img
                className={`mkt-phone ${
                  STEPS[step].blurbIndex === 1 ? "is-in" : ""
                }`}
                src={section.mobileImage}
                alt=""
              />
            )}
          </div>
        </div>

        <div className="mkt-dots" role="tablist" aria-label="Choose section">
          {SECTIONS.map((s, si) => (
            <button
              key={s.image}
              type="button"
              role="tab"
              aria-selected={si === sec}
              aria-label={`Go to: ${s.eyebrow}`}
              className={`mkt-dot ${si === sec ? "is-active" : ""}`}
              onClick={() => goToSection(si)}
            />
          ))}
        </div>
      </div>

      {/* Mobile: simple stacked sections */}
      <div className="mkt-stacked">
        {SECTIONS.map((s) => (
          <section className="mkt-stacked-section" key={s.image}>
            <span className="mkt-eyebrow">{s.eyebrow}</span>
            <h3 className="mkt-title">{s.heading}</h3>
            {s.blurbs.map((b, bi) => (
              <p className="mkt-blurb" key={bi}>
                {b}
              </p>
            ))}
            <div className="mkt-frame">
              <img className="mkt-shot" src={s.image} alt={s.caption} />
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
